import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { productsQuery, type Product } from '../api/inventory.ts';
import { ApiProblem } from '../api/me.ts';
import { addLine, quoteQuery, quotesQuery, type Quote } from '../api/quotes.ts';
import { roomsQuery } from '../api/venues.ts';
import { icons } from '../components/icons.ts';
import {
  ItemPicker,
  PartlyAdded,
  type PickedEntry,
  type PickerItem,
} from '../components/item-picker.tsx';
import { RoomPicker, roomForLine } from './produced-event.tsx';
import { LineForm } from './quote-line-form.tsx';

// Adding lines to a quote with the item picker (CB-REQ QUO-01; AVenue Design System ItemPicker,
// approved by the operator 2026-10-17, replacing the product select). The operator's decisions:
// - a pick goes on at its quantity with every day of the hire charged; days, discount and tax are
//   changed afterwards with the line's own Change form;
// - on a produced event, picks go into the room chosen above the picker until it is changed;
// - a row says how many the company owns, not what is free for this quote's window: that figure is
//   on each line once it is held (QUO-03), and the picker's availability bar waits for its query.
//
// A product charged by the hour or the kilometre needs its measure, which a pick does not have. The
// API refuses it as `measure-required`, naming the unit. One at a time, the full line form opens for
// that item with the picker kept behind it, list and search intact; in a built list it is refused
// with a sentence saying to add it on its own.

type Detail = { productId: string; quantity: number; unit: 'hour' | 'km' };

const measureUnit = (error: unknown): 'hour' | 'km' | null =>
  error instanceof ApiProblem && error.type === 'measure-required'
    ? error.extensions['unit'] === 'km'
      ? 'km'
      : 'hour'
    : null;

export function toPickerItems(products: readonly Product[], quote: Quote): PickerItem[] {
  const onQuote = new Map<string, number>();
  for (const l of quote.lines)
    onQuote.set(l.productId, (onQuote.get(l.productId) ?? 0) + l.quantity);
  const included = (id: string) => quote.inclusions.some((i) => i.productId === id && !i.declined);
  // INV-A14 at v1.990: this venue's own equipment is sold for it on a resale or direct-bill job,
  // beyond what its hire includes. The database refuses it where the venue is the client.
  const sellsVenueGear = quote.supplyModel === 'resale' || quote.supplyModel === 'direct_bill';
  return (
    products
      // A venue's own equipment as its hire includes it here (INV-A14 at v1.983), or sold for it.
      .filter(
        (p) =>
          !p.ownerVenueId ||
          included(p.id) ||
          (sellsVenueGear && p.ownerVenueId === quote.venue?.id),
      )
      .map((p) => {
        const group = p.ownerVenueId ? 'venue' : p.tracking === 'service' ? 'service' : 'hire';
        return {
          id: p.id,
          name: p.name,
          group,
          icon: group === 'venue' ? icons.venue : group === 'service' ? icons.service : icons.gear,
          meta:
            [p.category?.name, p.manufacturer, p.model].filter(Boolean).join(' · ') || undefined,
          note:
            group === 'venue'
              ? included(p.id)
                ? 'Included'
                : 'Charged'
              : group === 'hire'
                ? `${p.stockOnHand} owned`
                : undefined,
          onQuote: onQuote.get(p.id),
        } satisfies PickerItem;
      })
  );
}

export function QuoteLinePicker({
  quote,
  seesPrices,
  onDone,
}: {
  quote: Quote;
  seesPrices: boolean;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const products = useQuery(productsQuery);
  const roomsApply = quote.type === 'produced_event' && quote.venue !== null;
  const rooms = useQuery({ ...roomsQuery(quote.venue?.id ?? ''), enabled: roomsApply });
  const [roomId, setRoomId] = useState<string | null>(null);
  const [unaddedRoom, setUnaddedRoom] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const nameOf = (id: string) => products.data?.find((p) => p.id === id)?.name ?? 'one of these';

  // Lines go on one at a time, so a list can be refused part-way: what went on is named in
  // PartlyAdded, and the picker takes it off the list so it is never added twice. The quote is
  // refreshed whatever happened.
  const add = useMutation({
    mutationFn: async ({
      entries,
      mode,
    }: {
      entries: readonly PickedEntry[];
      mode: 'quick' | 'multi';
    }): Promise<void | 'handled'> => {
      const room = roomsApply
        ? await roomForLine(queryClient, quote.venue!.id, roomId, unaddedRoom)
        : null;
      if (roomsApply && unaddedRoom) {
        setRoomId(room);
        setUnaddedRoom(null);
      }
      const added: string[] = [];
      for (const entry of entries) {
        try {
          await addLine(quote.id, {
            productId: entry.id,
            quantity: entry.quantity,
            ...(roomsApply ? { roomId: room } : {}),
          });
        } catch (error) {
          const unit = measureUnit(error);
          if (unit && mode === 'quick') {
            setDetail({ productId: entry.id, quantity: entry.quantity, unit });
            return 'handled';
          }
          const message = unit
            ? `${nameOf(entry.id)} needs its ${unit === 'km' ? 'kilometres' : 'hours'}: add it on its own.`
            : error instanceof Error
              ? error.message
              : String(error);
          if (added.length > 0 || unit) throw new PartlyAdded(message, added);
          throw error;
        }
        added.push(entry.id);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: quoteQuery(quote.id).queryKey });
      void queryClient.invalidateQueries({ queryKey: quotesQuery.queryKey });
    },
  });

  const roomName = rooms.data?.find((r) => r.id === roomId)?.name ?? unaddedRoom ?? undefined;
  return (
    <>
      {detail ? (
        <LineForm
          quote={quote}
          seesPrices={seesPrices}
          startWith={{ ...detail, roomId }}
          onDone={() => setDetail(null)}
        />
      ) : null}
      {/* Kept mounted behind the form, so its search and list are there when the form closes. */}
      <div hidden={detail !== null}>
        <ItemPicker
          items={toPickerItems(products.data ?? [], quote)}
          loading={products.isPending}
          label="Add a line"
          scopeLabel={roomName}
          onAdd={(entries, mode) => add.mutateAsync({ entries, mode })}
          onClose={onDone}
          above={
            roomsApply ? (
              <RoomPicker
                venueId={quote.venue!.id}
                value={roomId}
                onChange={setRoomId}
                onNaming={setUnaddedRoom}
              />
            ) : quote.type === 'produced_event' ? (
              <p className="text-sm text-ink-soft">No venue yet: lines go in no room.</p>
            ) : null
          }
        />
      </div>
    </>
  );
}
